import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Share2,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  X,
  Phone,
  Mail,
  Copy,
  ExternalLink,
  Navigation,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  MapPin,
  Repeat,
  Clock,
  Calendar as CalendarIcon,
  Euro,
  Gamepad2,
  LayoutGrid,
  Users,
  Tag as TagIcon,
  DoorOpen,
  Timer,
  CheckCircle,
  XCircle,
  CalendarCheck,
  Trophy,
  ChevronRight as ChevronRightIcon,
  MessageCircle,
} from 'lucide-react';
import seriesService, { Series, SeriesEvent } from '../services/seriesService';
import StartChatButton from '../components/StartChatButton';
import FavoriteButton from '../components/profile/FavoriteButton';

const TEAL = '#3FCED2';
const WARN = '#F59E0B';

function pad2(n?: number): string {
  return String(typeof n === 'number' ? n : 0).padStart(2, '0');
}

function formatTime(t?: { hour?: number; minute?: number } | null): string {
  if (!t) return '';
  return `${pad2(t.hour)}:${pad2(t.minute)} Uhr`;
}

function formatDateDe(epoch?: number): string {
  if (!epoch) return '';
  const d = new Date(epoch);
  const weekdayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  return `${weekdayNames[d.getDay()]}, ${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

interface DetailItem {
  label: string;
  value: string;
  icon: React.ReactNode;
}

function buildSeriesDetails(series: Series): DetailItem[] {
  const items: DetailItem[] = [];

  if (series.schedule && series.schedule.type) {
    items.push({
      label: 'Zeitplan',
      value: seriesService.formatScheduleText(series.schedule),
      icon: <Repeat size={22} style={{ color: TEAL }} />,
    });
  }
  if (series.doorsOpen) {
    items.push({ label: 'Einlass ab', value: formatTime(series.doorsOpen), icon: <DoorOpen size={22} style={{ color: TEAL }} /> });
  }
  if (series.entryDeadlineOnSite) {
    items.push({ label: 'Anmeldeschluss vor Ort', value: formatTime(series.entryDeadlineOnSite), icon: <Timer size={22} style={{ color: TEAL }} /> });
  }
  if (series.start) {
    items.push({ label: 'Serienbeginn', value: formatTime(series.start), icon: <Clock size={22} style={{ color: TEAL }} /> });
  }
  if (typeof series.entryFee === 'number') {
    items.push({
      label: 'Startgeld',
      value: `${series.entryFee.toFixed(2).replace('.', ',')} €`,
      icon: <Euro size={22} style={{ color: TEAL }} />,
    });
  }
  if (series.gameMode && series.gameMode.length > 0) {
    items.push({ label: 'Spielmodus', value: series.gameMode, icon: <Gamepad2 size={22} style={{ color: TEAL }} /> });
  }
  if (typeof series.boards === 'number') {
    items.push({ label: 'Boards', value: `${series.boards} Boards`, icon: <LayoutGrid size={22} style={{ color: TEAL }} /> });
  }
  if (typeof series.entryLimit === 'number') {
    items.push({ label: 'Teilnehmerlimit', value: `${series.entryLimit} Plätze`, icon: <Users size={22} style={{ color: TEAL }} /> });
  }
  if (Array.isArray(series.categories) && series.categories.length > 0) {
    items.push({ label: 'Kategorie', value: series.categories.join(', '), icon: <TagIcon size={22} style={{ color: TEAL }} /> });
  }
  return items;
}

const INITIAL_EVENTS = 5;

const SeriesDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState<Series | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<SeriesEvent[]>([]);
  const [pastEvents, setPastEvents] = useState<SeriesEvent[]>([]);
  const [notFound, setNotFound] = useState(false);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDescriptionExpanded, setDescExpanded] = useState(false);
  const [isDetailsExpanded, setDetailsExpanded] = useState(false);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [externalLink, setExternalLink] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    try {
      const res = await seriesService.getSeriesBySlug(slug);
      const s: Series | null = res?.data || null;
      if (!s || typeof s !== 'object') {
        setNotFound(true);
        setSeries(null);
        return;
      }
      if (s.visible === false) {
        setNotFound(true);
        setSeries(null);
        return;
      }
      setSeries(s);

      const seriesId = s.id || s._id;
      if (seriesId) {
        try {
          const [up, all] = await Promise.all([
            seriesService.getUpcomingEventsBySeries(seriesId),
            seriesService.getEventsBySeries(seriesId),
          ]);
          if (Array.isArray(up?.data)) setUpcomingEvents(up.data);
          if (Array.isArray(all?.data)) {
            const now = Date.now();
            const past = all.data
              .filter((e) => typeof e.date === 'number' && (e.date as number) < now)
              .sort((a, b) => (b.date || 0) - (a.date || 0));
            setPastEvents(past);
          }
        } catch (e) {
          // ignore event errors
        }
      }
    } catch (err) {
      console.error('Fehler beim Laden der Serie:', err);
      setNotFound(true);
      setSeries(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (copyToast) {
      const tid = setTimeout(() => setCopyToast(null), 2000);
      return () => clearTimeout(tid);
    }
  }, [copyToast]);

  const allImages = useMemo<string[]>(() => {
    if (!series) return [];
    const list: string[] = [];
    if (series.image) list.push(series.image);
    if (Array.isArray(series.images)) {
      for (const img of series.images) {
        if (img && !list.includes(img)) list.push(img);
      }
    }
    return list;
  }, [series]);

  const lightboxList = useMemo(
    () => allImages.map((id) => seriesService.buildFileUrl(id) || ''),
    [allImages]
  );

  const detailItems = useMemo(() => (series ? buildSeriesDetails(series) : []), [series]);
  const visibleDetails = useMemo(() => (isDetailsExpanded ? detailItems : detailItems.slice(0, 3)), [detailItems, isDetailsExpanded]);

  const visibleUpcoming = useMemo(
    () => (showAllUpcoming ? upcomingEvents : upcomingEvents.slice(0, INITIAL_EVENTS)),
    [showAllUpcoming, upcomingEvents]
  );

  const description = series?.description || '';
  const isDescriptionLong = description.length > 300;
  const displayedDescription =
    isDescriptionExpanded || !isDescriptionLong
      ? description
      : `${description.substring(0, 300)}...`;

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = series?.name ? `${series.name} | DartCloud` : 'DartCloud Turnierserie';
    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, url: shareUrl });
        return;
      }
    } catch (_e) { /* user cancelled */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyToast('Link in Zwischenablage kopiert');
    } catch (_e) {
      setCopyToast('Teilen nicht möglich');
    }
  };

  const handleCopy = async (text: string, toastMsg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyToast(toastMsg);
    } catch (_e) {
      setCopyToast('Kopieren fehlgeschlagen');
    }
  };

  const handleOpenMaps = () => {
    if (!series?.address) return;
    const { latitude, longitude, label } = series.address;
    let url: string;
    if (typeof latitude === 'number' && typeof longitude === 'number') {
      url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    } else if (label) {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`;
    } else {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCallPhone = (phone?: string | null) => {
    if (!phone) return;
    window.location.href = `tel:${phone.replace(/\s+/g, '')}`;
  };

  const handleSendEmail = (email?: string | null) => {
    if (!email) return;
    window.location.href = `mailto:${email}`;
  };

  const handleOpenEntryLink = (url?: string | null) => {
    if (!url) return;
    setExternalLink(url);
  };

  const previousImage = () => {
    if (allImages.length <= 1) return;
    setCurrentImageIndex((idx) => (idx > 0 ? idx - 1 : allImages.length - 1));
  };
  const nextImage = () => {
    if (allImages.length <= 1) return;
    setCurrentImageIndex((idx) => (idx < allImages.length - 1 ? idx + 1 : 0));
  };
  const openImageLightbox = (idx: number) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-zinc-700 border-t-white rounded-full animate-spin" />
          <div className="text-sm text-zinc-400">Serie laden...</div>
        </div>
      </div>
    );
  }

  if (notFound || !series) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-zinc-500" />
          <h1 className="text-2xl font-bold mb-2">Turnierserie nicht gefunden</h1>
          <p className="text-zinc-400 mb-6">Die gesuchte Turnierserie existiert nicht oder ist nicht öffentlich.</p>
          <button
            onClick={() => navigate('/turnierserien')}
            className="px-5 py-2.5 rounded-lg bg-white text-black font-semibold hover:bg-zinc-200 transition"
          >
            Zur Turnierserien-Übersicht
          </button>
        </div>
      </div>
    );
  }

  const metaDescription =
    description.length > 0
      ? description.length > 160 ? `${description.substring(0, 157)}...` : description
      : `Turnierserie ${series.name || ''} auf DartCloud${series.address?.label ? ` - ${series.address.label}` : ''}`;
  const heroImageUrl = seriesService.buildFileUrl(allImages[0]);
  const currentImageUrl = seriesService.buildFileUrl(allImages[currentImageIndex]);
  const addressLabel = seriesService.formatAddress(series.address);
  const hasCoords = typeof series.address?.latitude === 'number' && typeof series.address?.longitude === 'number';
  const scheduleText = series.schedule ? seriesService.formatScheduleText(series.schedule) : '';
  const hasContact = !!(series.phone || series.email);
  const hasEntryLink = !!(series.entryLink && series.entryLink.length > 0);

  return (
    <>
      <Helmet>
        <title>{series.name ? `${series.name} | DartCloud` : 'Turnierserie | DartCloud'}</title>
        <meta name="description" content={metaDescription} />
        {heroImageUrl && <meta property="og:image" content={heroImageUrl} />}
        <meta property="og:title" content={series.name ? `${series.name} | DartCloud` : 'Turnierserie | DartCloud'} />
        <meta property="og:description" content={metaDescription} />
        <link rel="canonical" href={`https://dartcloudapp.de/turnierserie/${slug}`} />
      </Helmet>

      {/* HERO HEADER */}
      <section
        className="detail-hero w-full border-b border-zinc-800"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-10 py-5 sm:py-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 transition text-white"
              aria-label="Zurück"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-3">
              <FavoriteButton
                id={series.id || series._id || ''}
                type="SERIES"
                className="p-3 rounded-xl border border-zinc-700 bg-zinc-800 hover:bg-zinc-700"
              />
              <ActionButton icon={<Share2 size={20} />} onClick={handleShare} />
            </div>
          </div>

          <div className="mt-4">
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
              style={{ backgroundColor: `${TEAL}26` }}
            >
              <Trophy size={14} style={{ color: TEAL }} />
              <span className="text-xs font-semibold tracking-wider" style={{ color: TEAL }}>TURNIERSERIE</span>
            </div>
            <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold text-white leading-tight -tracking-[0.5px] break-words">
              {series.name}
            </h1>
            {(scheduleText || series.address?.label) && (
              <div className="mt-4 flex flex-wrap gap-2">
                {scheduleText && <HeroTag icon={<Repeat size={14} style={{ color: TEAL }} />} label={scheduleText} />}
                {series.address?.label && (
                  <HeroTag icon={<MapPin size={14} style={{ color: TEAL }} />} label={series.address.label} />
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-10 py-6 sm:py-10">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8 space-y-8">
            {/* Image Gallery */}
            {allImages.length > 0 && (
              <Section title="Galerie">
                <div className="rounded-2xl overflow-hidden bg-zinc-900 shadow-xl relative" style={{ height: 400 }}>
                  {currentImageUrl ? (
                    <img
                      src={currentImageUrl}
                      alt={`${series.name} Bild ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => openImageLightbox(currentImageIndex)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600">
                      <Trophy size={64} />
                    </div>
                  )}
                  {currentImageUrl && (
                    <button
                      onClick={() => openImageLightbox(currentImageIndex)}
                      className="absolute bottom-4 left-4 flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/70 text-white text-xs font-medium hover:bg-black/85 transition"
                    >
                      <Maximize2 size={16} />
                      Vollbild
                    </button>
                  )}
                  {allImages.length > 1 && (
                    <>
                      <button onClick={previousImage} className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center" aria-label="Vorheriges Bild">
                        <ChevronLeft size={26} />
                      </button>
                      <button onClick={nextImage} className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center" aria-label="Nächstes Bild">
                        <ChevronRight size={26} />
                      </button>
                      <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-black/70 text-white text-xs font-semibold">
                        {currentImageIndex + 1} / {allImages.length}
                      </div>
                    </>
                  )}
                </div>
                {allImages.length > 1 && (
                  <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                    {allImages.map((id, idx) => {
                      const url = seriesService.buildFileUrl(id);
                      const selected = idx === currentImageIndex;
                      return (
                        <button
                          key={`${id}-${idx}`}
                          onClick={() => openImageLightbox(idx)}
                          className="shrink-0 rounded-xl overflow-hidden bg-zinc-800"
                          style={{ width: 100, height: 80, border: `${selected ? 3 : 1}px solid ${selected ? TEAL : '#3f3f46'}` }}
                        >
                          {url ? (
                            <img src={url} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-500"><Trophy size={20} /></div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </Section>
            )}

            {/* Description */}
            {description.length > 0 && (
              <Section title="Über die Serie">
                <p className="whitespace-pre-wrap text-zinc-300 leading-[1.8] text-[16px]">{displayedDescription}</p>
                {isDescriptionLong && (
                  <button
                    onClick={() => setDescExpanded((v) => !v)}
                    className="mt-3 inline-flex items-center gap-1 text-[15px] font-semibold"
                    style={{ color: TEAL }}
                  >
                    {isDescriptionExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                    {isDescriptionExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                )}
              </Section>
            )}

            {/* Series Details */}
            {detailItems.length > 0 && (
              <Section title="Seriendetails">
                <div className="space-y-3">
                  {visibleDetails.map((item, idx) => (
                    <DetailCard key={`${item.label}-${idx}`} item={item} />
                  ))}
                </div>
                {!isDetailsExpanded && detailItems.length > 3 && (
                  <button
                    onClick={() => setDetailsExpanded(true)}
                    className="mt-4 inline-flex items-center gap-1 text-[15px] font-semibold"
                    style={{ color: TEAL }}
                  >
                    Alle Details anzeigen
                    <ChevronDown size={20} />
                  </button>
                )}
              </Section>
            )}

            {/* Events */}
            <Section title="Termine">
              {upcomingEvents.length === 0 ? (
                <p className="text-zinc-500 italic text-sm">Keine kommenden Termine</p>
              ) : (
                <div className="space-y-2">
                  {visibleUpcoming.map((event) => (
                    <EventTile
                      key={event.id || event._id || `${event.date}`}
                      event={event}
                      showRegisterButton={
                        !!(series.entryEnabled && series.registrationType === 'DARTCLOUD' && event.status === 'SCHEDULED')
                      }
                    />
                  ))}
                  {upcomingEvents.length > INITIAL_EVENTS && (
                    <button
                      onClick={() => setShowAllUpcoming((v) => !v)}
                      className="w-full mt-2 py-3 rounded-xl bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 transition text-zinc-400 font-semibold inline-flex items-center justify-center gap-1.5"
                    >
                      {showAllUpcoming ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      {showAllUpcoming
                        ? 'Weniger anzeigen'
                        : `${upcomingEvents.length - INITIAL_EVENTS} weitere Termine anzeigen`}
                    </button>
                  )}
                </div>
              )}

              {pastEvents.length > 0 && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowPastEvents((v) => !v)}
                    className="w-full py-3 rounded-xl bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 transition text-zinc-400 font-semibold inline-flex items-center justify-center gap-1.5"
                  >
                    {showPastEvents ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    {showPastEvents
                      ? 'Vergangene Termine ausblenden'
                      : `${pastEvents.length} vergangene Termine anzeigen`}
                  </button>
                  {showPastEvents && (
                    <div className="mt-2 space-y-2">
                      {pastEvents.slice(0, 10).map((event) => (
                        <EventTile
                          key={event.id || event._id || `${event.date}`}
                          event={event}
                          showRegisterButton={false}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Section>
          </div>

          {/* SIDEBAR */}
          <aside className="lg:col-span-4 mt-8 lg:mt-0 space-y-5">
            {hasEntryLink && (
              <SidebarCard title="Serienanmeldung" icon={<ExternalLink size={20} style={{ color: TEAL }} />}>
                <p className="text-sm text-zinc-400 leading-[1.5]">
                  Die Anmeldung für diese Turnierserie erfolgt über eine externe Website.
                </p>
                <p className="mt-3 text-xs text-zinc-500 break-all">{series.entryLink}</p>
                <button
                  onClick={() => handleOpenEntryLink(series.entryLink)}
                  className="mt-4 w-full px-4 py-3 rounded-xl font-semibold inline-flex items-center justify-center gap-2 transition"
                  style={{ backgroundColor: TEAL, color: '#fff' }}
                >
                  <ExternalLink size={18} />
                  Zur Anmeldung
                </button>
              </SidebarCard>
            )}

            <SidebarCard title="Veranstaltungsort" icon={<MapPin size={20} style={{ color: TEAL }} />}>
              <div className="flex items-start gap-3 mb-4">
                <MapPin size={20} style={{ color: TEAL }} className="shrink-0 mt-0.5" />
                <p className="text-sm text-zinc-300 leading-[1.5]">{addressLabel}</p>
              </div>
              <MapPreview
                latitude={series.address?.latitude || undefined}
                longitude={series.address?.longitude || undefined}
                hasCoords={hasCoords}
                onClickRoute={handleOpenMaps}
              />
            </SidebarCard>

            {hasContact && (
              <SidebarCard title="Kontakt" icon={<Phone size={20} style={{ color: TEAL }} />}>
                <div className="space-y-3">
                  {series.phone && (
                    <ContactItem
                      icon={<Phone size={20} style={{ color: TEAL }} />}
                      label={series.phone}
                      onClick={() => handleCallPhone(series.phone)}
                      onCopy={() => handleCopy(series.phone || '', 'Telefonnummer kopiert')}
                    />
                  )}
                  {series.email && (
                    <ContactItem
                      icon={<Mail size={20} style={{ color: TEAL }} />}
                      label={series.email}
                      onClick={() => handleSendEmail(series.email)}
                      onCopy={() => handleCopy(series.email || '', 'E-Mail kopiert')}
                    />
                  )}
                </div>
              </SidebarCard>
            )}

            {(series.id || series._id) && (
              <SidebarCard title="Nachricht" icon={<MessageCircle size={20} style={{ color: TEAL }} />}>
                <p className="text-sm text-zinc-400 leading-relaxed mb-4">
                  Hast du Fragen zur Turnierserie? Sende eine Nachricht über DartCloud.
                </p>
                <StartChatButton entryId={series.id || series._id} entryType="SERIES" />
              </SidebarCard>
            )}
          </aside>
        </div>
      </main>

      {lightboxOpen && lightboxList[lightboxIndex] && (
        <Lightbox
          src={lightboxList[lightboxIndex]}
          counter={`${lightboxIndex + 1} / ${lightboxList.length}`}
          showNav={lightboxList.length > 1}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setLightboxIndex((i) => (i > 0 ? i - 1 : lightboxList.length - 1))}
          onNext={() => setLightboxIndex((i) => (i < lightboxList.length - 1 ? i + 1 : 0))}
        />
      )}

      {externalLink && (
        <ExternalLinkModal
          url={externalLink}
          onCancel={() => setExternalLink(null)}
          onConfirm={() => {
            const url = externalLink;
            setExternalLink(null);
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
          }}
        />
      )}

      {copyToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-full shadow-lg text-sm font-medium bg-zinc-900 border border-zinc-700 text-white">
          {copyToast}
        </div>
      )}
    </>
  );
};

// ===================================================================
// Subcomponents
// ===================================================================

const ActionButton: React.FC<{ icon: React.ReactNode; onClick: () => void; active?: boolean }> = ({ icon, onClick, active }) => (
  <button
    onClick={onClick}
    className="p-3 rounded-xl border transition"
    style={{
      borderColor: active ? TEAL : '#3f3f46',
      backgroundColor: active ? `${TEAL}26` : '#27272a',
      color: active ? TEAL : '#d4d4d8',
    }}
  >
    {icon}
  </button>
);

const HeroTag: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-zinc-700 bg-zinc-800">
    {icon}
    <span className="text-xs font-medium text-zinc-300">{label}</span>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section>
    <h2 className="text-xl sm:text-[22px] font-bold text-white -tracking-[0.5px] mb-4">{title}</h2>
    {children}
  </section>
);

const DetailCard: React.FC<{ item: DetailItem }> = ({ item }) => (
  <div className="flex items-center gap-4 p-4 rounded-2xl bg-zinc-900 border border-zinc-800">
    <div
      className="p-2.5 rounded-xl"
      style={{ backgroundColor: `${TEAL}1A` }}
    >
      {item.icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[13px] text-zinc-400">{item.label}</div>
      <div className="text-[16px] font-semibold text-white">{item.value}</div>
    </div>
  </div>
);

const EventTile: React.FC<{ event: SeriesEvent; showRegisterButton: boolean }> = ({ event, showRegisterButton }) => {
  const isCancelled = event.status === 'CANCELLED';
  const isCompleted = event.status === 'COMPLETED';
  const statusColor = isCancelled ? '#ef4444' : isCompleted ? '#a1a1aa' : '#3FCED2';
  const statusIcon = isCancelled ? <XCircle size={20} color={statusColor} /> : isCompleted ? <CheckCircle size={20} color={statusColor} /> : <CalendarCheck size={20} color={statusColor} />;
  const dateStr = formatDateDe(event.date);

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-900 border border-zinc-800">
      <div className="shrink-0">{statusIcon}</div>
      <div className="flex-1 min-w-0">
        <div
          className="text-[15px] font-medium text-white truncate"
          style={isCancelled ? { textDecoration: 'line-through' } : undefined}
        >
          {dateStr}
        </div>
        {isCancelled && event.cancelledReason && (
          <div className="text-xs text-red-400 truncate">{event.cancelledReason}</div>
        )}
        {event.notes && (
          <div className="text-xs text-zinc-400 truncate">{event.notes}</div>
        )}
      </div>
      {showRegisterButton && (
        <button
          className="px-3 py-1.5 rounded-full text-sm font-semibold border"
          style={{ color: '#3FCED2', borderColor: '#3FCED2' }}
        >
          Anmelden
        </button>
      )}
    </div>
  );
};

const SidebarCard: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <section className="rounded-2xl bg-zinc-900 border border-zinc-800 p-5 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${TEAL}1A` }}>
        {icon}
      </div>
      <h3 className="text-[17px] font-bold text-white">{title}</h3>
    </div>
    {children}
  </section>
);

const ContactItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  onCopy: () => void;
}> = ({ icon, label, onClick, onCopy }) => (
  <div className="flex items-center gap-3">
    <button onClick={onClick} className="flex-1 min-w-0 flex items-center gap-3 py-1.5 hover:opacity-80 transition">
      {icon}
      <span className="text-sm text-zinc-300 truncate">{label}</span>
      <ChevronRightIcon size={14} className="text-zinc-500 ml-auto shrink-0" />
    </button>
    <button
      onClick={onCopy}
      className="p-2 rounded-lg border border-zinc-800 hover:bg-zinc-800 transition text-zinc-400 hover:text-white"
      aria-label="Kopieren"
    >
      <Copy size={14} />
    </button>
  </div>
);

const MapPreview: React.FC<{
  latitude?: number;
  longitude?: number;
  hasCoords: boolean;
  onClickRoute: () => void;
}> = ({ latitude, longitude, hasCoords, onClickRoute }) => {
  const iframeSrc = hasCoords && typeof latitude === 'number' && typeof longitude === 'number'
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - 0.005},${latitude - 0.0025},${longitude + 0.005},${latitude + 0.0025}&layer=mapnik&marker=${latitude},${longitude}`
    : null;

  return (
    <div className="relative w-full overflow-hidden rounded-2xl bg-zinc-800" style={{ height: 180 }}>
      {iframeSrc ? (
        <iframe
          src={iframeSrc}
          title="Karte"
          className="w-full h-full border-0 pointer-events-none"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500">
          <MapPin size={48} />
        </div>
      )}
      <button
        onClick={onClickRoute}
        className="absolute bottom-0 left-0 right-0 py-3 flex items-center justify-center gap-2 text-white text-sm font-semibold"
        style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.8))' }}
      >
        <Navigation size={18} />
        Route in Maps öffnen
      </button>
    </div>
  );
};

const Lightbox: React.FC<{
  src: string;
  counter: string;
  showNav: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}> = ({ src, counter, showNav, onClose, onPrev, onNext }) => (
  <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4" onClick={onClose}>
    <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="absolute top-4 right-4 p-2 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white" aria-label="Schließen">
      <X size={24} />
    </button>
    <img src={src} alt="Vollbild" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
    {showNav && (
      <>
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white flex items-center justify-center" aria-label="Vorheriges Bild">
          <ChevronLeft size={28} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white flex items-center justify-center" aria-label="Nächstes Bild">
          <ChevronRight size={28} />
        </button>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-zinc-800/80 text-white text-sm">
          {counter}
        </div>
      </>
    )}
  </div>
);

const ExternalLinkModal: React.FC<{
  url: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ url, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-[65] bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-3 mb-3">
        <AlertCircle size={28} style={{ color: WARN }} />
        <h3 className="text-lg font-bold text-white">Externe Weiterleitung</h3>
      </div>
      <p className="text-sm text-zinc-300">Du wirst zu einer externen Webseite weitergeleitet, um dich für diese Turnierserie anzumelden.</p>
      <div className="mt-3 p-3 rounded-lg bg-zinc-800 text-xs text-zinc-400 break-all">{url}</div>
      <p className="mt-3 text-xs italic text-zinc-500">
        DartCloud ist nicht für den Inhalt externer Webseiten verantwortlich.
      </p>
      <div className="mt-6 flex gap-3">
        <button onClick={onCancel} className="flex-1 px-4 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium">
          Abbrechen
        </button>
        <button onClick={onConfirm} className="flex-1 px-4 py-3 rounded-xl font-semibold inline-flex items-center justify-center gap-2" style={{ backgroundColor: TEAL, color: '#fff' }}>
          <ExternalLink size={18} />
          Weiter
        </button>
      </div>
    </div>
  </div>
);

export default SeriesDetailPage;
